import { ChangeDetectionStrategy, Component, Input, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AvatarComponent, AvatarSize } from '../../components/avatar/avatar.component';

export type SymbolLogoSize = 'small' | 'regular' | 'large';

/**
 * This component will display a logo via the svgString if provided.
 * If no logo is available, an avatar will be displayed.
 * If there is a symbol with letter characters available, it will display the first alphabetical character available.
 * Otherwise, a blank avatar will be displayed.
 */
@Component({
  imports: [
    NgClass,
    AvatarComponent,
    NgTemplateOutlet
  ],
  selector: 'td-wb-symbol-logo',
  templateUrl: 'symbol-logo.component.html',
  styleUrls: ['symbol-logo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SymbolLogoComponent {
  @Input() svgString: string;
  @Input() size: SymbolLogoSize = 'regular';
  @Input() symbol: string;
  @Input() description: string;

  constructor(private sanitizer: DomSanitizer) {}

  getSVGImageUrl(svgString: string): SafeUrl {
    return this.sanitizer.sanitize(SecurityContext.URL, 'data:image/svg+xml;base64,' + svgString);
  }

  showLogo(): boolean {
    return this.svgString !== undefined && this.svgString !== '';
  }

  getInitials(): string {
    const firstTwoCharsofSymbol = this.symbol?.substring(0, 2);
    let symbolText = (firstTwoCharsofSymbol?.match(/[a-z]/i) || [])?.pop();
    if(symbolText === undefined && this.description !== undefined) {
      symbolText = (this.description?.match(/[a-z]/i) || [])?.pop();
    }
    return symbolText ?? '';
  }

  isSmallLogo(): boolean {
    return this.size === 'small';
  }

  isLargeLogo(): boolean {
    return this.size === 'large';
  }

  getAvatarSize(): AvatarSize {
    if (this.size === 'small') {
      return 'extra-small';
    } else if (this.size === 'large') {
      return 'large';
    } else {
      return 'small';
    }
  }
}
